import { useEffect, useRef, useState } from "react";
import GoogleMap from "@shared/v2/GoogleMap/GoogleMap";
import { connect, ConnectedProps } from "react-redux";
import CustomOverlay from "../../../SearchListings/components/CustomOverlay";
import * as creators from "../../actions/creators";
import MapViewportManager from "./MapViewportManager";
import MarketReportMarker, { getMarketReportMarkers } from "./MarketReportMarker";

import { useBrowserCoords } from "./helpers";

import type { AddMarketReportState, PolygonFeature, PolygonGeometry } from "./types";

const mapStateToProps = (state: { addMarketReport: AddMarketReportState }) => {
  const { addMarketReport } = state;

  return {
    coords: addMarketReport.form1.lat_lng,
    browserLatLon: addMarketReport.browserLatLon,
    radius: addMarketReport.form1.radius,
    radiusType: addMarketReport.form1.radius_type,
    isDrawing: addMarketReport.isDrawing,
    searchLocation: addMarketReport.form1.searchLocation,
    searchType: addMarketReport.form1.search_type,
    hasSearch: addMarketReport.form1.searchLocation !== null,
    geoJSON: addMarketReport.form1.geo_json,
    boundry: addMarketReport.form1.boundry_coordinates,
    locationData: addMarketReport.locationData,
    currentName: addMarketReport.savedMarketReport?.name ?? null,
  };
};

const connector = connect(mapStateToProps, creators);

type Props = ConnectedProps<typeof connector>;

const DEFAULT_CENTER = { lat: 48.45, lng: -122.29 };

const normalizePolygonPath = (geometry?: PolygonGeometry) => {
  const coordinates = geometry?.coordinates?.[0];

  if (!coordinates?.length) {
    return [];
  }

  const polygon = coordinates.map(([lng, lat]) => ({ lat, lng }));

  if (polygon.length > 1) {
    const firstPoint = polygon[0];
    const lastPoint = polygon[polygon.length - 1];

    if (firstPoint.lat === lastPoint.lat && firstPoint.lng === lastPoint.lng) {
      polygon.pop();
    }
  }

  return polygon;
};

const Map = ({
  coords = null,
  browserLatLon = null,
  boundry = null,
  setDrawing,
  setLatLon,
  isDrawing,
  searchType,
  geoJSON = null,
  radius = 0,
  radiusType = null,
  setFormData,
  hasSearch,
  locationData = null,
  setLocationData,
  searchLocation = null,
  currentName = null,
}: Props) => {
  const [clearRequestKey, setClearRequestKey] = useState(0);
  const previousIsDrawing = useRef(isDrawing);
  const fallbackSearchType = searchLocation?.source === "GoogleAPI" ? "radius" : "area";

  useEffect(() => {
    if (previousIsDrawing.current && !isDrawing && !geoJSON) {
      setClearRequestKey((currentKey) => currentKey + 1);
    }

    previousIsDrawing.current = isDrawing;
  }, [isDrawing, geoJSON]);

  const handleDrawingStateChange = (nextIsDrawing: boolean) => {
    setDrawing(nextIsDrawing);
    setLocationData(null);

    if (nextIsDrawing) {
      setFormData({
        search_type: "area",
        name: currentName || "Custom Area",
      });
      return;
    }

    if (useBrowserCoords(coords, browserLatLon) && browserLatLon) {
      setLatLon(browserLatLon);
    }

    setFormData({
      search_type: fallbackSearchType,
    });
  };

  const handlePolygonChange = (feature: PolygonFeature) => {
    if (!feature?.geometry?.coordinates?.[0]?.length) {
      setFormData({
        geo_json: null,
        boundry_coordinates: null,
        search_type: fallbackSearchType,
      });
      return;
    }

    setFormData({
      geo_json: feature.geometry,
      boundry_coordinates: null,
      search_type: "area",
      name: currentName || "Custom Area",
    });
  };

  const defaultPolygon = normalizePolygonPath(geoJSON || boundry);
  const markers = getMarketReportMarkers(locationData, isDrawing, geoJSON);
  const centerPoint = coords?.length === 2 ? coords : browserLatLon?.length === 2 ? browserLatLon : null;

  return (
    <GoogleMap
      onChange={handlePolygonChange}
      onDrawingStateChange={handleDrawingStateChange}
      clearRequestKey={clearRequestKey}
      defaultPolygon={defaultPolygon}
      mapProps={{
        className: "tw-absolute tw-inset-0 add-market-report-map",
        defaultCenter: centerPoint ? { lat: centerPoint[0], lng: centerPoint[1] } : DEFAULT_CENTER,
        defaultZoom: centerPoint ? 10 : 4,
        minZoom: 6,
        maxZoom: 16,
        mapTypeControl: false,
        rotateControl: false,
        scaleControl: false,
        streetViewControl: false,
        clickableIcons: false,
      }}
    >
      <MapViewportManager
        coords={coords}
        boundry={boundry}
        geoJSON={geoJSON}
        hasSearch={hasSearch}
        isDrawing={isDrawing}
        locationData={locationData}
        radius={radius}
        radiusType={radiusType}
        searchType={searchType}
      />
      {markers.map(({ id, marker, type }) => (
        // @ts-expect-error props
        <CustomOverlay
          key={id}
          position={{ lat: marker.lat, lng: marker.lng }}
          pixelOffset={{ x: 0, y: 0 }}
          zIndex="initial"
        >
          <MarketReportMarker marker={marker} type={type} />
        </CustomOverlay>
      ))}
    </GoogleMap>
  );
};

export default connector(Map);
